/**
 * The home page's four counts, each a real query against an endpoint this repository already ships.
 *
 * No new API surface. `SupportRequestList.total` is an exact server-computed count regardless of page
 * size, so `pageSize: 1` asks for the smallest payload that still carries the number this screen
 * needs — the same field the queue's own pagination already reads.
 *
 * Four independent requests rather than one aggregate endpoint, because there is no aggregate
 * endpoint and inventing one is backend work outside this task. Independent also means one slow or
 * failed count does not block the other three — each stat renders its own loading and, on failure,
 * simply keeps showing its skeleton rather than a stat nobody can act on. A screen that cannot tell
 * you the true count should not guess at one.
 */
import type { SupportRequestListQuery } from '@apuestapr/contracts';
import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.ts';
import { createSupportAdminApi } from '../support/api.ts';

export interface DashboardStat {
  readonly status: 'loading' | 'ready' | 'error';
  readonly value: number | null;
}

const loadingStat: DashboardStat = { status: 'loading', value: null };

export interface DashboardStats {
  readonly newRequests: DashboardStat;
  readonly unassignedRequests: DashboardStat;
  readonly resolvedRequests: DashboardStat;
}

/**
 * One support-queue count, minimally paged. `assignee: 'unassigned'` reuses the same closed filter
 * vocabulary the queue's own filter form sends.
 */
const useSupportCount = (
  client: ApiClient,
  query: Omit<SupportRequestListQuery, 'page' | 'pageSize'>,
): DashboardStat => {
  const [stat, setStat] = useState<DashboardStat>(loadingStat);
  // Serialised, so a caller passing a fresh object literal each render does not reload forever — the
  // same reason `useSupportQueue` keys its own effect this way rather than on the object identity.
  const key = JSON.stringify(query);

  useEffect(() => {
    let active = true;
    // Reset after the effect returns, matching `useRequest` in `api/request-state.ts`: the initial
    // state is already loading, so this covers a changed query rather than the mount.
    queueMicrotask(() => {
      if (active) setStat(loadingStat);
    });
    const api = createSupportAdminApi(client);
    const parsed = JSON.parse(key) as Omit<SupportRequestListQuery, 'page' | 'pageSize'>;
    void api.list({ ...parsed, page: 1, pageSize: 1 }).then((result) => {
      if (!active) return;
      setStat(
        result.ok
          ? { status: 'ready', value: result.data.total }
          : { status: 'error', value: null },
      );
    });
    return () => {
      active = false;
    };
  }, [client, key]);

  return stat;
};

export const useDashboardStats = (client: ApiClient): DashboardStats => ({
  newRequests: useSupportCount(client, { status: 'new' }),
  unassignedRequests: useSupportCount(client, { assignee: 'unassigned' }),
  resolvedRequests: useSupportCount(client, { status: 'resolved' }),
});
